/**
 * ScheduleFormStep
 * Feature: 005-clinic-scheduling (T013)
 *
 * Container component that renders existing schedules for a patient
 * with add/edit/remove actions. Tabs for "Recorrente" and "Avulso".
 */

import { useState, useCallback } from 'react'
import { format } from 'date-fns'
import {
  CalendarDays,
  CalendarPlus,
  Trash2,
  Loader2,
  Clock,
  Repeat,
  MapPin,
  CalendarRange,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import { useSchedulingProfessionals } from '../hooks/use-scheduling-professionals'
import {
  useRecurringSchedules,
  useCreateRecurringSchedule,
  useDeactivateRecurringSchedule,
} from '../hooks/use-recurring-schedules'
import {
  useCancelSession,
  useCreateOneOffAppointment,
  useOneOffAppointments,
} from '../hooks/use-appointments'
import { useOverlapPolicy } from '../hooks/use-overlap-policy'
import { useOverlapCheck } from '../hooks/use-overlap-check'
import { RecurringScheduleForm } from './RecurringScheduleForm'
import { OneOffAppointmentForm } from './OneOffAppointmentForm'
import { OverlapWarningDialog } from './OverlapWarningDialog'
import {
  DAY_OF_WEEK_LABELS,
  FREQUENCY_LABELS,
  type DayOfWeek,
  type Frequency,
  type CreateRecurringScheduleFormData,
  type CreateOneOffAppointmentFormData,
  type OverlapCheckResult,
  type RecurringSchedule,
} from '../types/scheduling.types'

interface ScheduleFormStepProps {
  patientId: string
}

function formatDateLabel(date: string | null | undefined) {
  if (!date) return null
  const [year, month, day] = date.split('-')
  if (!year || !month || !day) return date
  return `${day}/${month}/${year}`
}

function getFirstOccurrenceDate(schedule: RecurringSchedule) {
  const [year, month, day] = schedule.effective_from.split('-').map(Number) as [
    number | undefined,
    number | undefined,
    number | undefined,
  ]
  if (!year || !month || !day) return null

  const date = new Date(year, month - 1, day)
  if (Number.isNaN(date.getTime())) return null

  const daysUntil = (Number(schedule.day_of_week) - date.getDay() + 7) % 7
  date.setDate(date.getDate() + daysUntil)

  return format(date, 'yyyy-MM-dd')
}

function getScheduleWindowState(schedule: RecurringSchedule) {
  const today = format(new Date(), 'yyyy-MM-dd')
  const firstOccurrence = getFirstOccurrenceDate(schedule)
  const firstOccurrenceLabel = firstOccurrence
    ? formatDateLabel(firstOccurrence)
    : null
  const endLabel = formatDateLabel(schedule.effective_until)

  if (
    schedule.effective_until &&
    firstOccurrence &&
    schedule.effective_until < firstOccurrence
  ) {
    return {
      badge: 'Sem ocorrências',
      badgeClassName: 'border-red-200 bg-red-50 text-red-700',
      detail: `Termina em ${endLabel}, antes da primeira ocorrência em ${firstOccurrenceLabel}.`,
      detailClassName: 'text-red-600',
    }
  }

  if (schedule.effective_until && schedule.effective_until < today) {
    return {
      badge: 'Encerrado',
      badgeClassName: 'border-amber-200 bg-amber-50 text-amber-700',
      detail: `Este agendamento terminou em ${endLabel}.`,
      detailClassName: 'text-amber-700',
    }
  }

  if (schedule.effective_from > today) {
    return {
      badge: 'Agendado para iniciar',
      badgeClassName: 'border-blue-200 bg-blue-50 text-blue-700',
      detail: `Começa em ${formatDateLabel(schedule.effective_from)}.`,
      detailClassName: 'text-blue-700',
    }
  }

  return {
    badge: schedule.effective_until ? 'Vigente' : 'Sem término',
    badgeClassName: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    detail: null,
    detailClassName: '',
  }
}

export function ScheduleFormStep({ patientId }: ScheduleFormStepProps) {
  const { toast } = useToast()
  const [showForm, setShowForm] = useState(false)
  const [activeTab, setActiveTab] = useState('recurring')
  const [overlapResult, setOverlapResult] = useState<OverlapCheckResult | null>(
    null
  )
  const [pendingSubmit, setPendingSubmit] = useState<(() => void) | null>(null)

  // Data hooks
  const { data: schedules, isLoading: schedulesLoading } =
    useRecurringSchedules(patientId)
  const { data: oneOffAppointments, isLoading: appointmentsLoading } =
    useOneOffAppointments(patientId)
  const { data: professionals } = useSchedulingProfessionals()
  const { data: policy } = useOverlapPolicy()

  // Mutation hooks
  const createRecurring = useCreateRecurringSchedule()
  const deactivateSchedule = useDeactivateRecurringSchedule()
  const createOneOff = useCreateOneOffAppointment()
  const cancelSession = useCancelSession()
  const { checkOverlap } = useOverlapCheck()

  const professionalOptions = professionals ?? []

  const handleOverlapDetected = useCallback(
    (result: OverlapCheckResult) => {
      if (result.has_conflicts && result.policy === 'not_allowed') {
        toast({
          title: 'Conflito de horário',
          description:
            'Não é possível agendar neste horário pois há conflito com outro atendimento.',
          variant: 'destructive',
        })
      }
      // For 'allowed_with_warning', the overlap dialog will be shown on submit
    },
    [toast]
  )

  const handleRecurringSubmit = async (
    data: CreateRecurringScheduleFormData
  ) => {
    // Check overlap before submitting
    if (policy && policy !== 'allowed') {
      const today = new Date()
      const targetDay = Number(data.day_of_week)
      const daysUntil = (targetDay - today.getDay() + 7) % 7 || 7
      const nextDate = new Date(today)
      nextDate.setDate(today.getDate() + daysUntil)

      const result = await checkOverlap(
        {
          professional_ids: data.professional_ids,
          session_date: format(nextDate, 'yyyy-MM-dd'),
          start_time: data.start_time,
          end_time: data.end_time,
        },
        policy
      )

      if (result.has_conflicts) {
        if (policy === 'not_allowed') {
          toast({
            title: 'Conflito de horário',
            description:
              'Não é possível agendar neste horário. Altere o horário ou os profissionais.',
            variant: 'destructive',
          })
          return
        }

        if (policy === 'allowed_with_warning') {
          setOverlapResult(result)
          setPendingSubmit(() => async () => {
            await executeRecurringCreate(data)
          })
          return
        }
      }
    }

    await executeRecurringCreate(data)
  }

  const executeRecurringCreate = async (
    data: CreateRecurringScheduleFormData
  ) => {
    const result = await createRecurring.mutateAsync(data)
    if (result.success) {
      toast({
        title: 'Agendamento criado',
        description: 'O agendamento recorrente foi criado com sucesso.',
      })
      setShowForm(false)
    } else {
      toast({
        title: 'Erro',
        description: result.error ?? 'Erro ao criar agendamento.',
        variant: 'destructive',
      })
    }
  }

  const handleOneOffSubmit = async (data: CreateOneOffAppointmentFormData) => {
    // Check overlap before submitting
    if (policy && policy !== 'allowed') {
      const result = await checkOverlap(
        {
          professional_ids: data.professional_ids,
          session_date: data.session_date,
          start_time: data.start_time,
          end_time: data.end_time,
        },
        policy
      )

      if (result.has_conflicts) {
        if (policy === 'not_allowed') {
          toast({
            title: 'Conflito de horário',
            description:
              'Não é possível agendar neste horário. Altere o horário ou os profissionais.',
            variant: 'destructive',
          })
          return
        }

        if (policy === 'allowed_with_warning') {
          setOverlapResult(result)
          setPendingSubmit(() => async () => {
            await executeOneOffCreate(data)
          })
          return
        }
      }
    }

    await executeOneOffCreate(data)
  }

  const executeOneOffCreate = async (data: CreateOneOffAppointmentFormData) => {
    const result = await createOneOff.mutateAsync(data)
    if (result.success) {
      toast({
        title: 'Atendimento agendado',
        description: 'O atendimento avulso foi agendado com sucesso.',
      })
      setShowForm(false)
    } else {
      toast({
        title: 'Erro',
        description: result.error ?? 'Erro ao agendar atendimento.',
        variant: 'destructive',
      })
    }
  }

  const handleDeactivate = async (scheduleId: string) => {
    const result = await deactivateSchedule.mutateAsync({
      id: scheduleId,
      cancel_future_sessions: true,
    })
    if (result.success) {
      toast({
        title: 'Agendamento desativado',
        description: 'O agendamento e as sessões futuras foram cancelados.',
      })
    } else {
      toast({
        title: 'Erro',
        description: result.error ?? 'Erro ao desativar agendamento.',
        variant: 'destructive',
      })
    }
  }

  const handleCancelOneOff = async (sessionId: string) => {
    const result = await cancelSession.mutateAsync({
      session_id: sessionId,
      scope: 'this_only',
    })

    if (result.success) {
      toast({
        title: 'Atendimento cancelado',
        description: 'O atendimento avulso foi cancelado.',
      })
    } else {
      toast({
        title: 'Erro',
        description: result.error ?? 'Erro ao cancelar atendimento.',
        variant: 'destructive',
      })
    }
  }

  const handleOverlapConfirm = () => {
    pendingSubmit?.()
    setOverlapResult(null)
    setPendingSubmit(null)
  }

  const handleOverlapCancel = () => {
    setOverlapResult(null)
    setPendingSubmit(null)
  }

  const activeSchedules = (schedules ?? []).filter((s) => s.is_active)
  const upcomingOneOffAppointments = oneOffAppointments ?? []
  const isLoadingLists = schedulesLoading || appointmentsLoading
  const hasItems =
    activeSchedules.length > 0 || upcomingOneOffAppointments.length > 0

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Agendamento</h3>
        {!showForm && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowForm(true)}
          >
            <CalendarPlus className="mr-2 h-4 w-4" />
            Novo Agendamento
          </Button>
        )}
      </div>

      {/* Existing schedules list */}
      {isLoadingLists ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : hasItems ? (
        <div className="space-y-2">
          {activeSchedules.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Recorrentes
              </p>
              {activeSchedules.map((schedule) => (
                <Card key={schedule.id}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <Repeat className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">
                          {
                            DAY_OF_WEEK_LABELS[
                              schedule.day_of_week as DayOfWeek
                            ]
                          }
                        </p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>
                            {schedule.start_time} — {schedule.end_time}
                          </span>
                          <Badge variant="secondary">
                            {FREQUENCY_LABELS[schedule.frequency as Frequency]}
                          </Badge>
                          {(() => {
                            const windowState = getScheduleWindowState(
                              schedule
                            )
                            return (
                              <Badge
                                variant="outline"
                                className={windowState.badgeClassName}
                              >
                                {windowState.badge}
                              </Badge>
                            )
                          })()}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <CalendarRange className="h-3 w-3" />
                            Início:{' '}
                            {formatDateLabel(schedule.effective_from)}
                          </span>
                          <span>
                            Término:{' '}
                            {formatDateLabel(schedule.effective_until) ??
                              'sem data'}
                          </span>
                        </div>
                        {(() => {
                          const windowState = getScheduleWindowState(schedule)
                          if (!windowState.detail) return null

                          return (
                            <p
                              className={`mt-1 text-xs ${windowState.detailClassName}`}
                            >
                              {windowState.detail}
                            </p>
                          )
                        })()}
                        {schedule.professionals?.length > 0 && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {schedule.professionals
                              .map(
                                (sp) =>
                                  sp.professional?.full_name ?? 'Profissional'
                              )
                              .join(', ')}
                          </p>
                        )}
                        {schedule.location_text && (
                          <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span>{schedule.location_text}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeactivate(schedule.id)}
                      disabled={deactivateSchedule.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {upcomingOneOffAppointments.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Avulsos futuros
              </p>
              {upcomingOneOffAppointments.map((appointment) => (
                <Card key={appointment.id}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <CalendarDays className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">
                          {appointment.session_date
                            .split('-')
                            .reverse()
                            .join('/')}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>
                            {appointment.start_time} — {appointment.end_time}
                          </span>
                          <Badge variant="outline">Avulso</Badge>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {[
                            appointment.primary_professional?.full_name,
                            ...(appointment.additional_professionals ?? []).map(
                              (sp) =>
                                sp.professional?.full_name ?? 'Profissional'
                            ),
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                        {appointment.location_text && (
                          <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span>{appointment.location_text}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelOneOff(appointment.id)}
                      disabled={cancelSession.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      ) : (
        !showForm && (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            <CalendarDays className="mx-auto h-8 w-8 mb-2" />
            <p>Nenhum agendamento definido.</p>
            <p>Clique em "Novo Agendamento" para adicionar.</p>
          </div>
        )
      )}

      {/* New schedule form */}
      {showForm && (
        <Card>
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="recurring">Recorrente</TabsTrigger>
                <TabsTrigger value="one-off">Avulso</TabsTrigger>
              </TabsList>

              <TabsContent value="recurring">
                <RecurringScheduleForm
                  patientId={patientId}
                  professionals={professionalOptions}
                  onSubmit={handleRecurringSubmit}
                  onOverlapDetected={handleOverlapDetected}
                  isSubmitting={createRecurring.isPending}
                />
              </TabsContent>

              <TabsContent value="one-off">
                <OneOffAppointmentForm
                  patientId={patientId}
                  professionals={professionalOptions}
                  onSubmit={handleOneOffSubmit}
                  onOverlapDetected={handleOverlapDetected}
                  isSubmitting={createOneOff.isPending}
                />
              </TabsContent>
            </Tabs>

            <div className="flex justify-start pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowForm(false)}
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overlap warning dialog */}
      <OverlapWarningDialog
        open={!!overlapResult}
        onOpenChange={(open) => {
          if (!open) handleOverlapCancel()
        }}
        conflicts={overlapResult?.conflicts ?? []}
        onConfirm={handleOverlapConfirm}
        onCancel={handleOverlapCancel}
      />
    </div>
  )
}
